import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { PlatformProvider } from '@/providers/PlatformProvider';
import { AuthProvider } from '@/providers/AuthProvider';
import { CartProvider } from '@/providers/CartProvider';
import { router } from '@/routes';
import { ApiException } from '@/types/api';
import '@/styles/app.css';

/**
 * Query defaults tuned for a commerce frontend.
 *
 * The retry rule matters: retrying a 401, 403, 404 or a Coming Soon response
 * cannot succeed and only delays the correct UI state, so only genuine
 * transport and server failures are retried.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (error instanceof ApiException) {
          const permanent = ['UNAUTHENTICATED', 'FORBIDDEN', 'NOT_FOUND', 'FEATURE_COMING_SOON'];

          if (permanent.includes(error.code)) {
            return false;
          }
        }

        return failureCount < 2;
      },
    },
    mutations: {
      // Never retry a mutation automatically: a duplicated order or payment
      // is far worse than a failure the customer can retry deliberately (§27).
      retry: false,
    },
  },
});

const container = document.getElementById('root');

if (!container) {
  throw new Error('Root element #root was not found in the document.');
}

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      {/* Platform config gates rendering: routes cannot be resolved until
          feature flags are known — see PlatformProvider. AuthProvider sits
          inside it because route guards need both, and the session check
          should not delay the branded splash. */}
      <PlatformProvider>
        <AuthProvider>
          {/* Inside AuthProvider: the cart is scoped to the current identity,
              and merges from guest to customer on login. */}
          <CartProvider>
            <RouterProvider router={router} />
          </CartProvider>
        </AuthProvider>
      </PlatformProvider>
    </QueryClientProvider>
  </StrictMode>,
);
